<template>
    <div class="feedback-content">
        <div class="form-field required">
            <div class="title">Тема обращения</div>
            <div class="value">
                <Combobox v-model="model.theme"
                          :options="themes"
                          :toTitle="it => it.name"
                          :class="{ error: v.model.theme.$error }"
                          placeholder="Выберите тему обращения"
                          @input="selectTheme"
                />
            </div>
        </div>
        <div v-show="!hideProduct" :class="{ required: productRequired }" class="form-field">
            <div class="title">Название продукта</div>
            <div class="value">
                <Combobox v-model="model.product"
                          :resolve="getProduct"
                          :search="searchProducts"
                          :toTitle="it => it.name"
                          :class="{ error: v.model.product.$error }"
                          class="value select-moderators"
                          :placeholder="'Введите название продукта'"
                >
                    <template #absent="{ searchText }">
                        <template v-if="!searchText">Начните вводить название продукта</template>
                        <template v-else>Ничего не найдено</template>
                    </template>
                </Combobox>
            </div>
        </div>
        <div class="form-field required">
            <div class="title">Текст обращения</div>
            <div class="value">
                <textarea v-model="model.description"
                          :class="{ error: v.model.description.$error }"
                          class="description"
                          maxlength="2000"
                          :placeholder="themeDescription" />
                <span class="inform">
                    <template v-if="model.description.length">
                        Осталось {{
                            remainingChar | pluralForm([
                                '%n символ', '%n символа', '%n символов',
                            ])
                        }}
                    </template>
                    <template v-else>
                        Максимум 2000 символов
                    </template>
                </span>
            </div>
        </div>

        <div class="form-field">
            <div class="title">Прикреплённые файлы</div>
            <div class="value">
                <DocumentsAdd :max-file-size="0"
                              :max-total-files-size="50"
                              :can-upload-document="false"
                              :documents.sync="model.documents"
                              class="documents-add"
                              entity-type=""
                />
            </div>
        </div>

        <div class="form-field ">
            <div class="value info">
                По вопросам работоспособности сайта можно обратиться в техническую поддержку
                <a href="https://helpme.rt.ru/tickets/create/otrs1/3806"
                   target="_blank">
                    Helpme
                </a>
            </div>
        </div>
    </div>
</template>


<script>
import DocumentsAdd from '@/common/page_comp/Documents/DocumentsAdd.vue';
import { equals, updateState } from '@/common/util/util.js';
import { toMap } from '@dsee/util/arrays.js';
import { getThemes, save } from '@/restapi/feedback.js';
import { save as saveDocument } from '@/restapi/document/document.js';
import { findProducts, loadBasicInfo } from '@/restapi/product/product.js';
import { showError } from '@dsee/components/popups/toast.js';
import { useVuelidate } from '@vuelidate/core';
import { maxLength, required, requiredIf } from '@vuelidate/validators';

const emptyModel = () => ({
    product: null,
    theme: null,
    description: '',
    documents: [],
});

export default {
    name: 'FeedbackContent',
    setup() {
        return {
            v: useVuelidate({ $autoDirty: true }),
        };
    },
    components: { DocumentsAdd },
    data() {
        return {
            model: emptyModel(),
            themes: [],

            responsibleUser: {},
        };
    },
    validations() {
        return {
            model: {
                theme: {
                    required,
                },
                product: {
                    requiredIf: requiredIf(this.productRequired),
                },
                description: {
                    required,
                    maxLength: maxLength(2000),
                },
            },
        };
    },
    computed: {
        productRequired() {
            return this.themesMap[this.model.theme]?.productRequired;
        },
        remainingChar() {
            return 2000 - this.model.description.length;
        },
        themeDescription() {
            return this.themesMap[this.model.theme]?.description || 'Введите текст обращения';
        },
        themesMap() {
            return toMap(this.themes);
        },
        hideProduct() {
            return this.model.theme === 4;
        },
    },
    async created() {
        this.reset();

        if (!this.themes.length)
            this.themes = await getThemes();
    },
    methods: {
        getProduct: loadBasicInfo,
        searchProducts(searchText) {
            return findProducts(searchText);
        },
        setInnerState(newModel) {
            updateState(this.model, newModel);
        },
        reset() {
            Object.assign(this.model, emptyModel());
        },
        async save() {
            const documents = [];
            try {
                for (const it of this.model.documents) {
                    await it.upload();

                    documents.push(it.id);
                }
            } catch (e) {
                showError('Произошла ошибка при сохранении документов');
                throw e;
            }

            await save({
                ...this.model,
                documents,
            });
        },
        selectTheme() {
            if (this.model.theme === 4)
                this.model.product = null;
        },
    },
    watch: {
        model: {
            deep: true,
            handler(value) {
                const changed = !equals(value, emptyModel());
                this.$emit('changed', changed);
            },
        },
    },
};
</script>


<style lang="scss" scoped>
.feedback-content {
    .annotations {
        background: rgba(16, 24, 40, 0.03);
        border-radius: 8px;
        padding: 20px;
    }

    .description {
        resize: vertical;
        min-height: 88px;
        color: var(--text-color);
        font-size: var(--text-font-size);
        line-height: var(--text-line-height);
    }

    .inform {
        color: var(--input-placeholder-color);
        font-size: 12px;
    }

    .info {
        border-top: 1px solid rgba(16, 24, 40, 0.25);
        padding-top: 8px;
        color: var(--label-color);
    }
}
</style>
